/**
 * Clean redraw model: the parsed P&ID graph (editor store nodes + edges) turned
 * into the indices the Clean redraw and Line map views draw from.
 *
 * Every link the extraction stores is kept here as-is. The views draw from these
 * links (a pipe end attached to a symbol is always drawn reaching that symbol), so
 * the picture can't show a connection the graph doesn't have, or hide one it does.
 */
import type { Edge, Node, TagNode } from '@/service/graph'

export type Kind = 'valve' | 'cv' | 'fe' | 'inst' | 'equip' | 'conn' | 'note' | 'fit' | 'tee' | 'other'
export type LineKind = 'pipe' | 'elec' | 'soft' | 'em' | 'unk'
export type TagRel = 'label' | 'attr' | 'line'
export type Box = [number, number, number, number]
export type Pt = [number, number]

export interface VNode {
  id: string
  k: 's' | 'c' | 't'
  bb: Box
  /** connection polylines in sheet coordinates */
  ln?: Pt[][]
  lk?: LineKind
  /** connection line type, e.g. "Pipe" */
  lt?: string
  /** connection line number */
  no?: string
  /** symbol: display category (valve, inst, …) used for colour */
  c?: string
  /** symbol: friendly type name, e.g. "Gate valve" */
  fr?: string
  /** symbol: its tag / label, e.g. "FIC1603" */
  tag?: string
  /** symbol: raw symbol_type */
  st?: string
  cf?: number
  /** tag: text */
  tx?: string
  lv: string[]
}

export interface Model {
  nodes: VNode[]
  /** [from, to, relation code] */
  edges: Array<[number, number, string]>
  W: number
  H: number
  nbr: Array<Set<number>>
  foldTags: number[][]
  pipeTags: number[][]
  tagOwner: Int32Array
  connUp: Array<Set<number>>
  connDown: Array<Set<number>>
  connTouch: Array<Set<number>>
  SYMS: number[]
  CONNS: number[]
  TAGS: number[]
  KIND: Array<Kind | null>
  TAGREL: Map<number, TagRel>
  orphanTags: number[]
  snapCache: Array<Snap | undefined>
  lineRunsCache?: LineRuns
}

export interface Snap { x: number; y: number; hz: boolean; onPipe: boolean }

export const PROC = new Set<LineKind>(['pipe', 'unk'])
export const SIGK = new Set<LineKind>(['elec', 'soft', 'em'])

const LINE_KIND: Record<string, LineKind> = {
  Pipe: 'pipe', 'Electric Signal': 'elec', 'Logic Data Software Signal': 'soft', Electromagnetic: 'em',
}
const REL: Record<string, string> = {
  touches_connection: 'tc', flows_to: 'ft', has_annotation: 'ha', has_label: 'hl', annotated_by: 'ab',
}
const LABEL_JUNK = new Set(['signal tee', 'right', 'left', 'up', 'down'])
const FIT: Record<string, string> = {
  piping_arrow_down: 'Flow arrow', piping_arrow_up: 'Flow arrow', piping_arrow_left: 'Flow arrow', piping_arrow_right: 'Flow arrow',
  piping_spec_break: 'Spec break', piping_reducer_concentric: 'Concentric reducer', piping_reducer_eccentric: 'Eccentric reducer',
  piping_flange: 'Flange', piping_spec_blind_open: 'Spectacle blind (open)', piping_spec_blind_closed: 'Spectacle blind (closed)',
  piping_insulation: 'Insulation',
}
const VALVE: Record<string, string> = {
  valve_gate: 'Gate valve', valve_drain: 'Drain valve', valve_check: 'Check valve', valve_ball: 'Ball valve',
  valve_globe: 'Globe valve', valve_control: 'Control valve',
}
const INST_KIND: Record<string, string> = {
  instrument_dcs: 'DCS', instrument_local_mounted: 'field', instrument_safety: 'SIS', instrument_special: 'special',
}

export const isNul = (v: unknown): boolean => v == null || v === '' || v === 'null' || v === 'None' || v === 'NA' || v === 'nan'
const sentence = (s: string) => { const t = s.trim().toLowerCase(); return t.charAt(0).toUpperCase() + t.slice(1) }

export function friendlyType(st: string): string {
  if (FIT[st]) return FIT[st]
  if (VALVE[st]) return VALVE[st]
  if (st === 'Signal Tee') return 'Signal tee'
  if (st === 'drawing_annotation') return 'Drawing note'
  if (st === 'drawing_to_drawing_connector') return 'Off-sheet connector'
  if (st === 'drawing_to_utility_connector') return 'Utility connector'
  if (st === 'equipment') return 'Equipment'
  if (st === 'instrument_pneumatic') return 'Pneumatic instrument'
  if (st === 'instrument_low') return 'Instrument'
  const m = /^(instrument_[a-z_]+)\s*\((.+)\)$/.exec(st)
  if (m) return `${sentence(m[2])} (${INST_KIND[m[1]] ?? 'inst'})`
  if (INST_KIND[st]) return `Instrument (${INST_KIND[st]})`
  return sentence(st.replace(/_/g, ' ')) || 'Symbol'
}

function categoryOf(name: string | undefined, st: string): string {
  if (st === 'Signal Tee') return 'tee'
  if (name === 'valve') return 'valve'
  if (name === 'instrument') return 'inst'
  if (name === 'equipment') return 'equip'
  if (name === 'drawing' || name === 'drawing_to_drawing_connector' || st.startsWith('drawing_')) return 'ref'
  return 'fit'
}

export function kindOf(n: VNode): Kind {
  const st = n.st ?? ''
  if (n.c === 'tee') return 'tee'
  if (st.startsWith('valve_')) return st === 'valve_control' ? 'cv' : 'valve'
  if (/FLOW VALVE|AIR OPERATED VALVE|SHUTDOWN VALVE|CONTROL VALVE/.test(st)) return 'cv'
  if (/FLOW ELEMENT/.test(st)) return 'fe'
  if (st.startsWith('instrument')) return 'inst'
  if (st === 'equipment') return 'equip'
  if (st.startsWith('drawing_to')) return 'conn'
  if (st === 'drawing_annotation') return 'note'
  if (st.startsWith('piping_')) return 'fit'
  return 'other'
}

/** "LINESTRING (x y, …)" / "MULTILINESTRING ((…), (…))" → polylines */
export function parseLines(wkt: string | undefined): Pt[][] {
  if (!wkt) return []
  const parts = wkt.trim().toUpperCase().startsWith('MULTI') ? (wkt.match(/\(([^()]+)\)/g) ?? []) : [wkt.slice(wkt.indexOf('('))]
  return parts.map(p => {
    const v = (p.match(/-?\d+(?:\.\d+)?/g) ?? []).map(Number)
    const pts: Pt[] = []
    for (let i = 0; i + 1 < v.length; i += 2) pts.push([Math.round(v[i]), Math.round(v[i + 1])])
    return pts
  }).filter(l => l.length >= 2)
}

const boxOf = (n: Node): Box | null => {
  if (n.x == null || n.y == null) return null
  return [n.x, n.y, n.x + (n.width ?? 0), n.y + (n.height ?? 0)]
}

export function buildModel(storeNodes: Node[], storeEdges: Edge[]): Model {
  const nodes: VNode[] = []
  const idx = new Map<string, number>()
  for (const n of storeNodes) {
    if (n.type !== 'symbol' && n.type !== 'tag' && n.type !== 'connection') continue
    const lv = [n.pnid_level_i ?? '', n.pnid_level_ii ?? '', n.pnid_level_iii ?? '']
    if (n.type === 'connection') {
      const ln = parseLines(n.geometry_wkt)
      if (!ln.length) continue
      const xs = ln.flat().map(p => p[0]), ys = ln.flat().map(p => p[1])
      const v: VNode = { id: n.id, k: 'c', bb: [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)], ln, lv,
        lk: LINE_KIND[n.pnid_level_iii ?? ''] ?? 'unk', lt: n.pnid_level_iii || 'Unclassified line' }
      if (!isNul(n.lineNumber)) v.no = n.lineNumber
      idx.set(n.id, nodes.length); nodes.push(v)
      continue
    }
    const bb = boxOf(n)
    if (!bb) continue
    if (n.type === 'symbol') {
      const st = n.symbol_type && n.symbol_type !== 'null' ? n.symbol_type : (n.name ?? '')
      const fr = friendlyType(st)
      const v: VNode = { id: n.id, k: 's', bb, lv, st, fr, c: categoryOf(n.name, st) }
      const lab = n.label?.trim()
      if (!isNul(lab) && lab && !LABEL_JUNK.has(lab.toLowerCase()) && lab !== fr) v.tag = lab
      if (n.confidence != null) v.cf = Math.round(n.confidence * 100) / 100
      idx.set(n.id, nodes.length); nodes.push(v)
    } else {
      idx.set(n.id, nodes.length); nodes.push({ id: n.id, k: 't', bb, lv, tx: (n as TagNode).content || n.annotation_text || '' })
    }
  }
  const edges: Array<[number, number, string]> = []
  const seen = new Set<string>()
  for (const e of storeEdges) {
    const a = idx.get(e.fromNodeId), b = idx.get(e.toNodeId)
    if (a == null || b == null) continue
    edges.push([a, b, REL[e.relation ?? ''] ?? 'x']); seen.add(`${a}|${b}`)
  }
  // the editor's parse keeps two more text links as fields rather than edges: the instrument a
  // reading was given to (ownerSymbolId) and the pipe a label is written along (writtenAlong)
  for (const n of storeNodes) {
    if (n.type !== 'tag') continue
    const b = idx.get(n.id); if (b == null) continue
    const own = idx.get(n.ownerSymbolId ?? n.extraProperties?.owner_symbol_id ?? '')
    const along = idx.get(n.writtenAlong ?? n.extraProperties?.written_along ?? '')
    if (own != null && !seen.has(`${own}|${b}`)) { edges.push([own, b, 'ow']); seen.add(`${own}|${b}`) }
    if (along != null && !seen.has(`${along}|${b}`)) { edges.push([along, b, 'wa']); seen.add(`${along}|${b}`) }
  }
  const nN = nodes.length
  const mk = <T,>(f: () => T) => Array.from({ length: nN }, f)
  const m: Model = {
    nodes, edges, W: 0, H: 0, nbr: mk(() => new Set<number>()), foldTags: mk(() => [] as number[]), pipeTags: mk(() => [] as number[]),
    tagOwner: new Int32Array(nN).fill(-1), connUp: mk(() => new Set<number>()), connDown: mk(() => new Set<number>()), connTouch: mk(() => new Set<number>()),
    SYMS: [], CONNS: [], TAGS: [], KIND: [], TAGREL: new Map(), orphanTags: [], snapCache: new Array(nN),
  }
  for (const [a, b, r] of edges) {
    m.nbr[a].add(b); m.nbr[b].add(a)
    const A = nodes[a], B = nodes[b]
    if ((r === 'ha' || r === 'hl' || r === 'ow') && A.k === 's' && B.k === 't') {
      if (m.tagOwner[b] < 0) { m.tagOwner[b] = a; m.foldTags[a].push(b); m.TAGREL.set(b, r === 'hl' ? 'label' : 'attr') }
    } else if ((r === 'ab' || r === 'wa') && A.k === 'c' && B.k === 't') {
      if (m.tagOwner[b] < 0) { m.tagOwner[b] = a; m.pipeTags[a].push(b); m.TAGREL.set(b, 'line') }
    } else if (r === 'tc') {
      if (A.k === 's' && B.k === 'c') m.connTouch[b].add(a); else if (A.k === 'c' && B.k === 's') m.connTouch[a].add(b)
    } else if (r === 'ft') {
      if (A.k === 's' && B.k === 'c') m.connUp[b].add(a); else if (A.k === 'c' && B.k === 's') m.connDown[a].add(b)
    }
  }
  nodes.forEach((n, i) => {
    (n.k === 's' ? m.SYMS : n.k === 'c' ? m.CONNS : m.TAGS).push(i)
    m.KIND.push(n.k === 's' ? kindOf(n) : null)
    m.W = Math.max(m.W, n.bb[2]); m.H = Math.max(m.H, n.bb[3])
  })
  m.orphanTags = m.TAGS.filter(t => m.tagOwner[t] < 0)
  return m
}

// ---------- queries ----------
export const connSyms = (m: Model, c: number): number[] => [...new Set([...m.connUp[c], ...m.connDown[c], ...m.connTouch[c]])]
export const procDeg = (m: Model, i: number): number => { let d = 0; for (const c of m.nbr[i]) if (m.nodes[c].k === 'c' && PROC.has(m.nodes[c].lk!)) d++; return d }
export const splitTag = (t?: string): [string, string] => { const x = /^([A-Z]+)[-\s]?(\d.*)$/.exec(t ?? ''); return x ? [x[1], x[2]] : [t ?? '', ''] }
export const instStyle = (st: string): 'logic' | 'dcs' | 'pneu' | 'field' =>
  /^instrument_safety|LOGIC/.test(st) ? 'logic' : /^instrument_dcs/.test(st) ? 'dcs' : st === 'instrument_pneumatic' ? 'pneu' : 'field'
export const isTagBubble = (m: Model, i: number): boolean => m.KIND[i] === 'cv' && /^instrument/.test(m.nodes[i].st ?? '')
export const catOf = (m: Model, i: number): string => {
  const K = m.KIND[i]
  return K === 'valve' || K === 'cv' ? 'valve' : K === 'inst' || K === 'fe' ? 'inst' : K === 'equip' ? 'equip' : K === 'conn' || K === 'note' ? 'conn' : 'fit'
}
/** the symbol's own text, de-duplicated against its tag */
export function ftexts(m: Model, i: number): string[] {
  const out: string[] = [], seen = new Set([(m.nodes[i].tag ?? '').toLowerCase()])
  for (const t of m.foldTags[i]) { const x = (m.nodes[t].tx ?? '').trim(); if (x && !seen.has(x.toLowerCase())) { seen.add(x.toLowerCase()); out.push(x) } }
  return out
}
/** the symbol's attribute texts (has_annotation / owner links), e.g. "SET @ 64.00 GPM" */
export const foldAttrTexts = (m: Model, i: number): string[] =>
  m.foldTags[i].filter(t => m.TAGREL.get(t) !== 'label').map(t => (m.nodes[t].tx ?? '').trim()).filter(Boolean)
/** [name, second line] for a symbol */
export function symLabel(m: Model, i: number): [string, string] {
  const n = m.nodes[i], K = m.KIND[i], ft = ftexts(m, i)
  if (K === 'equip') { const t = ft.find(s => /\d/.test(s) && s.includes('-')) || (n.tag && n.tag !== 'M' ? n.tag : '') || 'Equipment'; return [t, ft.filter(s => s !== t && s !== 'M').join(' · ')] }
  if (K === 'conn') { const ref = n.tag && /\d/.test(n.tag) ? n.tag : ''; return ref ? [ref, ft.join(' · ')] : [ft[0] || 'Off-sheet', ft.slice(1).join(' · ')] }
  if (K === 'note') return [n.tag || ft[0] || 'Note', '']
  if (K === 'valve') return [[n.tag, ...ft].filter(Boolean).join(' '), '']
  return [n.tag || n.fr || '', '']
}
export const nodeName = (m: Model, i: number): string => {
  const n = m.nodes[i]
  return n.k === 's' ? (symLabel(m, i)[0] || n.fr || 'Symbol') : n.k === 'c' ? (n.no || n.lt || 'Line') : (n.tx || 'Text')
}

export function polyMid(lines: Pt[][]): { x: number; y: number; dx: number; dy: number; line: Pt[]; len: number } | null {
  let best = lines[0], bl = -1
  for (const l of lines) { let L = 0; for (let k = 1; k < l.length; k++) L += Math.hypot(l[k][0] - l[k - 1][0], l[k][1] - l[k - 1][1]); if (L > bl) { bl = L; best = l } }
  if (!best) return null
  let half = bl / 2
  for (let k = 1; k < best.length; k++) {
    const [x0, y0] = best[k - 1], [x1, y1] = best[k], L = Math.hypot(x1 - x0, y1 - y0)
    if (half <= L && L > 0) { const f = half / L; return { x: x0 + (x1 - x0) * f, y: y0 + (y1 - y0) * f, dx: (x1 - x0) / L, dy: (y1 - y0) / L, line: best, len: bl } }
    half -= L
  }
  return null
}

/** Where an inline symbol sits on its pipe: orientation, and its centre on the pipe. */
export function snap(m: Model, i: number): Snap {
  const cached = m.snapCache[i]; if (cached) return cached
  const b = m.nodes[i].bb, cx = (b[0] + b[2]) / 2, cy = (b[1] + b[3]) / 2, reach = Math.max(b[2] - b[0], b[3] - b[1]) * 1.6 + 30
  const ends: Pt[] = []; let sx = 0, sy = 0
  for (const c of m.nbr[i]) {
    const C = m.nodes[c]; if (C.k !== 'c' || !PROC.has(C.lk!)) continue
    let best: [Pt, Pt] | null = null, bd = Infinity
    for (const l of C.ln!) for (const [p, q] of [[l[0], l[1]], [l[l.length - 1], l[l.length - 2]]] as Array<[Pt, Pt]>) {
      const d = Math.hypot(p[0] - cx, p[1] - cy); if (d < bd) { bd = d; best = [p, q] }
    }
    if (best && bd < reach) { ends.push(best[0]); sx += Math.abs(best[1][0] - best[0][0]); sy += Math.abs(best[1][1] - best[0][1]) }
  }
  const hz = (sx || sy) ? sx >= sy : (b[2] - b[0]) >= (b[3] - b[1])
  let x = cx, y = cy
  if (ends.length >= 2) {
    const corner = Math.abs(ends[0][0] - ends[1][0]) > 8 && Math.abs(ends[0][1] - ends[1][1]) > 8
    if (!corner) { x = (ends[0][0] + ends[1][0]) / 2; y = (ends[0][1] + ends[1][1]) / 2 }
  } else if (ends.length === 1) { if (hz) y = ends[0][1]; else x = ends[0][0] }
  // pipe ends that miss the symbol's own box (a bridge drawn from a neighbour's corner) don't move it off its box
  if (hz && (y < b[1] - 2 || y > b[3] + 2)) y = cy
  if (!hz && (x < b[0] - 2 || x > b[2] + 2)) x = cx
  return (m.snapCache[i] = { x, y, hz, onPipe: ends.length > 0 })
}

export interface LineEnd { p: Pt; ok: boolean; s: number; extra?: boolean }
/**
 * Each end of a line and the symbol it attaches to. The graph's link decides
 * attachment (distance only decides which end), and every symbol the graph puts
 * on the line gets an end — so every stored link is drawn.
 */
export function lineEnds(m: Model, c: number): LineEnd[] {
  const S = connSyms(m, c), pts: Pt[] = []
  for (const l of m.nodes[c].ln!) pts.push(l[0], l[l.length - 1])
  const dist = (p: Pt, s: number) => { const b = m.nodes[s].bb; return Math.hypot(Math.max(b[0] - p[0], 0, p[0] - b[2]), Math.max(b[1] - p[1], 0, p[1] - b[3])) }
  const out: LineEnd[] = pts.map(p => {
    let best = Infinity, bs = -1
    for (const s of S) { const d = dist(p, s); if (d < best) { best = d; bs = s } }
    return { p, ok: best <= 45, s: best <= 45 ? bs : -1 }
  })
  if (S.length === 2 && pts.length === 2) {
    const [a, b] = S
    const [s0, s1] = dist(pts[0], a) + dist(pts[1], b) <= dist(pts[0], b) + dist(pts[1], a) ? [a, b] : [b, a]
    out[0] = { p: pts[0], ok: true, s: s0 }; out[1] = { p: pts[1], ok: true, s: s1 }
  }
  for (const s of S) if (!out.some(x => x.s === s)) {
    let best = Infinity, bp: Pt | null = null
    for (const p of pts) { const d = dist(p, s); if (d < best) { best = d; bp = p } }
    if (bp) out.push({ p: bp, ok: true, s, extra: true })
  }
  return out
}

// ---------- line numbers carried along pipe runs (Line map) ----------
export const METRO = ['#0072CE', '#00A651', '#F2A900', '#8E44AD', '#00A6D6', '#F57C00', '#7CB342', '#D81B9C', '#795548', '#1B998B', '#3F51B5', '#9E9D24', '#455A64', '#B8860B', '#5C6BC0', '#26A69A']
export const isLineNo = (t?: string): boolean => !!t && t.length >= 8 && /\d/.test(t) && /-/.test(t)
export interface LineRun { id: string; conns: number[]; len: number; color: string; k: number }
export interface LineRuns { lineOf: Map<number, string>; lines: LineRun[]; byId: Map<string, LineRun>; seeded: number; proc: number }

function endDir(m: Model, c: number, s: number): Pt | null {
  const b = m.nodes[s].bb, cx = (b[0] + b[2]) / 2, cy = (b[1] + b[3]) / 2
  let best: Pt | null = null, bd = Infinity
  for (const l of m.nodes[c].ln!) for (const [p, q] of [[l[0], l[1]], [l[l.length - 1], l[l.length - 2]]] as Array<[Pt, Pt]>) {
    const d = Math.hypot(p[0] - cx, p[1] - cy); if (d < bd) { bd = d; best = [q[0] - p[0], q[1] - p[1]] }
  }
  if (!best) return null
  const n = Math.hypot(best[0], best[1]) || 1
  return [best[0] / n, best[1] / n]
}

/** Line numbers found on segments, carried along each run through inline items and straight through tees. */
export function lineRuns(m: Model): LineRuns {
  if (m.lineRunsCache) return m.lineRunsCache
  const lineOf = new Map<number, string>(), q: number[] = []
  let proc = 0
  for (const c of m.CONNS) {
    const n = m.nodes[c]; if (!PROC.has(n.lk!)) continue; proc++
    const t = isLineNo(n.no) ? n.no : m.pipeTags[c].map(x => (m.nodes[x].tx ?? '').trim()).find(isLineNo)
    if (t) { lineOf.set(c, t); q.push(c) }
  }
  const seeded = q.length
  for (let h = 0; h < q.length; h++) {
    const c = q[h], L = lineOf.get(c)!
    for (const s of connSyms(m, c)) {
      const K = m.KIND[s]; if (K === 'equip' || K === 'conn') continue
      const others = [...m.nbr[s]].filter(c2 => c2 !== c && m.nodes[c2].k === 'c' && PROC.has(m.nodes[c2].lk!))
      let next: number[] = []
      if (others.length === 1) next = others
      else if (others.length >= 2) { // a branch: the run carries on along the straight continuation only
        const d0 = endDir(m, c, s); let bd = -0.85
        if (d0) for (const c2 of others) { const d2 = endDir(m, c2, s); if (!d2) continue; const dot = d0[0] * d2[0] + d0[1] * d2[1]; if (dot < bd) { bd = dot; next = [c2] } }
      }
      for (const c2 of next) if (!lineOf.has(c2)) { lineOf.set(c2, L); q.push(c2) }
    }
  }
  const runs = new Map<string, LineRun>()
  for (const [c, L] of lineOf) {
    let r = runs.get(L); if (!r) runs.set(L, (r = { id: L, conns: [], len: 0, color: '', k: 0 }))
    r.conns.push(c); r.len += polyMid(m.nodes[c].ln!)?.len ?? 0
  }
  const lines = [...runs.values()].sort((a, b) => b.len - a.len)
  lines.forEach((r, k) => { r.color = METRO[k % METRO.length]; r.k = k })
  return (m.lineRunsCache = { lineOf, lines, byId: new Map(lines.map(r => [r.id, r])), seeded, proc })
}

/** Everything a selected / hovered item is linked to (for highlighting). */
export function litSet(m: Model, i: number, view: 'clean' | 'lines'): Set<number> {
  const S = new Set([i])
  if (m.nodes[i].k === 't' && m.tagOwner[i] >= 0) S.add(m.tagOwner[i])
  if (view === 'lines') {
    const R = lineRuns(m), ids = new Set<string>()
    if (m.nodes[i].k === 'c' && R.lineOf.has(i)) ids.add(R.lineOf.get(i)!)
    if (m.nodes[i].k === 's') for (const c of m.nbr[i]) if (R.lineOf.has(c)) ids.add(R.lineOf.get(c)!)
    for (const id of ids) for (const c of R.byId.get(id)!.conns) { S.add(c); for (const s of connSyms(m, c)) S.add(s) }
    if (ids.size) return S
  }
  for (const j of m.nbr[i]) { S.add(j); if (m.nodes[j].k === 'c' && m.nodes[i].k !== 'c') for (const q of m.nbr[j]) if (m.nodes[q].k === 's') S.add(q) }
  return S
}
